import { useCallback, useEffect, useState } from "react";
import { mediaList, type MediaItem } from "@/mocks/media";

const FAVORITES_KEY = "97video:favorites";
const HISTORY_KEY = "97video:history";

function readList(key: string): string[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: string[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore storage errors */
  }
}

const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    setFavorites(readList(FAVORITES_KEY));
    const sync = () => setFavorites(readList(FAVORITES_KEY));
    listeners.add(sync);
    window.addEventListener("storage", sync);
    return () => {
      listeners.delete(sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    const current = readList(FAVORITES_KEY);
    const next = current.includes(id) ? current.filter((item) => item !== id) : [id, ...current];
    writeList(FAVORITES_KEY, next);
    setFavorites(next);
    emit();
  }, []);

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggle, isFavorite, count: favorites.length };
}

export function useHistory() {
  const [historyIds, setHistoryIds] = useState<string[]>([]);

  useEffect(() => {
    setHistoryIds(readList(HISTORY_KEY));
    const sync = () => setHistoryIds(readList(HISTORY_KEY));
    listeners.add(sync);
    window.addEventListener("storage", sync);
    return () => {
      listeners.delete(sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const record = useCallback((id: string) => {
    const current = readList(HISTORY_KEY).filter((item) => item !== id);
    const next = [id, ...current].slice(0, 12);
    writeList(HISTORY_KEY, next);
    setHistoryIds(next);
    emit();
  }, []);

  const clear = useCallback(() => {
    writeList(HISTORY_KEY, []);
    setHistoryIds([]);
    emit();
  }, []);

  return { historyIds, record, clear };
}

export function getFavoriteItems(ids: string[]): MediaItem[] {
  return ids
    .map((id) => mediaList.find((item) => item.id === id))
    .filter((item): item is MediaItem => Boolean(item));
}

export function getHistoryItems(ids: string[]): MediaItem[] {
  return ids
    .map((id) => mediaList.find((item) => item.id === id))
    .filter((item): item is MediaItem => Boolean(item));
}